import React, { useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, fmtMoney } from "../lib/api";
import { toast } from "sonner";
import { Building2, LogOut, Plus, CreditCard, Trash2, Users } from "lucide-react";

function CorporateAuth() {
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [f, setF] = useState({ company_name: "", contact_name: "", email: "", password: "", phone: "", billing_address: "" });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const endpoint = mode === "login" ? "/corporate/login" : "/corporate/register";
      const payload = mode === "login" ? { email: f.email, password: f.password } : f;
      const { data } = await api.post(endpoint, payload);
      localStorage.setItem("lume_corp_token", data.token);
      localStorage.setItem("lume_corp_company", data.company_name);
      nav("/corporate/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not complete request");
    } finally { setBusy(false); }
  };

  return (
    <div className="section-container py-24 max-w-lg">
      <div className="lume-card p-8">
        <Building2 className="text-[color:var(--lume-gold)] mb-3" size={22}/>
        <div className="eyebrow mb-3">Corporate Portal</div>
        <h1 className="font-serif text-2xl text-white">{mode === "login" ? "Sign in" : "Create company account"}</h1>
        <p className="text-sm text-slate-400 mt-2">Book on behalf of multiple riders under a single billing profile with consolidated monthly invoices.</p>

        <div className="mt-6 space-y-4">
          {mode === "register" && <>
            <div><label className="field-label">Company Name</label><input className="input-lume" value={f.company_name} onChange={(e)=>setF({...f, company_name:e.target.value})} data-testid="corp-company-name"/></div>
            <div><label className="field-label">Contact Name</label><input className="input-lume" value={f.contact_name} onChange={(e)=>setF({...f, contact_name:e.target.value})} data-testid="corp-contact-name"/></div>
            <div><label className="field-label">Phone</label><input className="input-lume" value={f.phone} onChange={(e)=>setF({...f, phone:e.target.value})} data-testid="corp-phone"/></div>
            <div><label className="field-label">Billing Address</label><input className="input-lume" value={f.billing_address} onChange={(e)=>setF({...f, billing_address:e.target.value})} data-testid="corp-billing"/></div>
          </>}
          <div><label className="field-label">Email</label><input type="email" className="input-lume" value={f.email} onChange={(e)=>setF({...f, email:e.target.value})} data-testid="corp-email"/></div>
          <div><label className="field-label">Password</label><input type="password" className="input-lume" value={f.password} onChange={(e)=>setF({...f, password:e.target.value})} data-testid="corp-password"/></div>
          <button onClick={submit} disabled={busy} className="btn-gold w-full" data-testid="corp-submit-btn">
            {busy ? "Please wait…" : (mode === "login" ? "Sign In" : "Create Account")}
          </button>
          <button onClick={()=>setMode(mode==="login"?"register":"login")} className="text-xs text-slate-400 hover:text-white w-full text-center" data-testid="corp-toggle-mode">
            {mode==="login" ? "Need a company account? Register" : "Already registered? Sign in"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CorporateDashboard() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { data: me } = useQuery({ queryKey: ["corp-me"], queryFn: async () => (await api.get("/corporate/me")).data });
  const { data: bookings } = useQuery({ queryKey: ["corp-res"], queryFn: async () => (await api.get("/corporate/reservations")).data });
  const { data: riders = [] } = useQuery({ queryKey: ["corp-riders"], queryFn: async () => (await api.get("/corporate/riders")).data });
  const [rider, setRider] = useState({ name: "", email: "", phone: "" });

  const riderMut = useMutation({
    mutationFn: async (b) => (await api.post("/corporate/riders", b)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["corp-riders"] }); setRider({ name: "", email: "", phone: "" }); toast.success("Rider saved."); },
    onError: () => toast.error("Could not save rider."),
  });
  const delRiderMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/corporate/riders/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["corp-riders"] }),
  });
  const settleMut = useMutation({
    mutationFn: async (enabled) => (await api.patch("/corporate/auto-settle", { enabled })).data,
    onSuccess: (d) => { qc.invalidateQueries({ queryKey: ["corp-me"] }); toast.success(d.auto_settle ? "Auto-settle enabled — monthly invoices will charge your card." : "Auto-settle disabled."); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not update auto-settle."),
  });
  const saveCard = async () => {
    try {
      const { data } = await api.post("/corporate/billing/setup-session", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch { toast.error("Could not start card setup."); }
  };
  const bookFor = (r) => nav(`/booking?corporate=1&rider_name=${encodeURIComponent(r.name)}&rider_email=${encodeURIComponent(r.email)}&rider_phone=${encodeURIComponent(r.phone || "")}`);

  const logout = () => { localStorage.removeItem("lume_corp_token"); localStorage.removeItem("lume_corp_company"); nav("/corporate"); };

  return (
    <div className="section-container py-10">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow mb-2">Corporate Portal</div>
          <h1 className="font-serif text-3xl text-white">{me?.company_name || "Company"}</h1>
          <div className="text-sm text-slate-400 mt-1">{me?.contact_name} · {me?.email}</div>
        </div>
        <div className="flex gap-3">
          <Link to="/booking?corporate=1" className="btn-gold" data-testid="corp-new-booking-btn"><Plus size={14} className="inline mr-1"/>New Booking</Link>
          <button onClick={logout} className="btn-ghost" data-testid="corp-logout-btn"><LogOut size={14} className="inline mr-2"/>Sign out</button>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="lume-card p-5"><div className="eyebrow">Total Rides</div><div className="font-serif text-2xl text-white mt-2">{bookings?.reservations?.length ?? 0}</div></div>
        <div className="lume-card p-5"><div className="eyebrow">Completed Charges</div><div className="font-serif text-2xl text-white mt-2">{fmtMoney(bookings?.completed_total ?? 0)}</div></div>
        <div className="lume-card p-5"><div className="eyebrow">Projected Monthly</div><div className="font-serif text-2xl text-[color:var(--lume-gold-light)] mt-2">{fmtMoney(bookings?.projected_total ?? 0)}</div></div>
      </div>

      <div className="mt-10 lume-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-widest text-slate-400 border-b border-white/5">
              <th className="p-4">Reservation</th><th className="p-4">Rider</th><th className="p-4">Date</th><th className="p-4">Vehicle</th><th className="p-4">Fare</th><th className="p-4">Status</th>
            </tr>
          </thead>
          <tbody>
            {bookings?.reservations?.map(r => (
              <tr key={r.reservation_number} className="border-b border-white/5" data-testid={`corp-res-${r.reservation_number}`}>
                <td className="p-4 font-mono text-[color:var(--lume-gold-light)]">{r.reservation_number}</td>
                <td className="p-4"><div className="text-white">{r.customer_name}</div><div className="text-xs text-slate-500">{r.customer_email}</div></td>
                <td className="p-4 text-slate-300">{r.pickup_date} · {r.pickup_time}</td>
                <td className="p-4 text-slate-300 text-xs">{r.vehicle_class_name}</td>
                <td className="p-4 text-white">{fmtMoney(r.estimated_fare)}</td>
                <td className="p-4"><span className={`status-pill status-${r.status}`}>{r.status.replace("_"," ")}</span></td>
              </tr>
            ))}
            {(!bookings?.reservations || bookings.reservations.length === 0) &&
              <tr><td colSpan={6} className="p-8 text-center text-slate-500">No corporate bookings yet. Click New Booking to reserve on behalf of a rider.</td></tr>
            }
          </tbody>
        </table>
      </div>
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="lume-card p-6">
          <div className="eyebrow mb-2"><CreditCard size={12} className="inline mr-1"/>Billing Profile</div>
          {me?.stripe_default_pm ? (
            <div className="text-sm text-slate-300">
              <div>Card on file: <strong className="text-white capitalize">{me.card_brand} ···· {me.card_last4}</strong></div>
              <label className="flex items-center gap-3 mt-4 cursor-pointer" data-testid="auto-settle-toggle">
                <input type="checkbox" className="accent-[color:var(--lume-gold)]" checked={!!me.auto_settle}
                  onChange={(e) => settleMut.mutate(e.target.checked)} data-testid="auto-settle-checkbox"/>
                <span className="text-xs text-slate-400">Auto-charge this card when the monthly invoice is generated</span>
              </label>
            </div>
          ) : (
            <>
              <div className="text-sm text-slate-400">No card on file. Monthly invoices are sent by email for manual payment.</div>
              <button onClick={saveCard} className="btn-outline-gold mt-4" data-testid="save-card-btn">Save Card on File</button>
            </>
          )}
        </div>
        <div className="lume-card p-6">
          <div className="eyebrow mb-3"><Users size={12} className="inline mr-1"/>Rider Profiles</div>
          <div className="grid grid-cols-3 gap-2 mb-4">
            <input className="input-lume !py-2" placeholder="Name" value={rider.name} onChange={(e)=>setRider({...rider, name:e.target.value})} data-testid="rider-name"/>
            <input className="input-lume !py-2" placeholder="Email" value={rider.email} onChange={(e)=>setRider({...rider, email:e.target.value})} data-testid="rider-email"/>
            <input className="input-lume !py-2" placeholder="Phone" value={rider.phone} onChange={(e)=>setRider({...rider, phone:e.target.value})} data-testid="rider-phone"/>
          </div>
          <button className="btn-ghost !text-xs" disabled={!rider.name || !rider.email || riderMut.isPending} onClick={()=>riderMut.mutate(rider)} data-testid="rider-save-btn">Save Rider</button>
          <div className="mt-4 space-y-2">
            {riders.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-3 border border-white/5 rounded-lg px-3 py-2" data-testid={`rider-row-${r.id}`}>
                <div className="text-sm"><div className="text-white">{r.name}</div><div className="text-xs text-slate-500">{r.email}</div></div>
                <div className="flex gap-2">
                  <button className="btn-outline-gold !py-1.5 !px-3 !text-xs" onClick={()=>bookFor(r)} data-testid={`rider-book-${r.id}`}>Book</button>
                  <button className="btn-ghost !py-1.5 !px-2 !text-xs" onClick={()=>delRiderMut.mutate(r.id)} data-testid={`rider-del-${r.id}`}><Trash2 size={12}/></button>
                </div>
              </div>
            ))}
            {riders.length === 0 && <div className="text-xs text-slate-500">No riders yet — save frequent riders to re-book them in two taps.</div>}
          </div>
        </div>
      </div>
      <div className="mt-6 lume-card p-6 text-sm text-slate-400">
        <div className="eyebrow mb-2">Consolidated Billing</div>
        Each ride uses secure Stripe checkout. At month-end, all completed rides are consolidated into a single invoice{me?.auto_settle ? " — charged automatically to your card on file" : " emailed to"} <strong className="text-white">{me?.auto_settle ? "" : me?.email}</strong>.
      </div>
    </div>
  );
}

export default function Corporate() {
  const path = window.location.pathname;
  const token = localStorage.getItem("lume_corp_token");
  if (path === "/corporate/dashboard" && !token) return <Navigate to="/corporate" replace/>;
  if (path === "/corporate/dashboard") return <CorporateDashboard/>;
  if (token) return <Navigate to="/corporate/dashboard" replace/>;
  return <CorporateAuth/>;
}
